import { currencyLabel, monthNames, unitLabel } from './i18n.js';

// All money and volume figures arrive from the API as decimal strings and are
// only ever turned into numbers for display. Nothing here feeds back into a
// calculation.
//
// The interface is Arabic and right-to-left, but figures stay in Latin digits
// with thousands separators and dates stay dd/mm/yyyy. Every formatted value is
// therefore wrapped in a Unicode left-to-right isolate: without it, the
// bidirectional algorithm reorders runs like "12/08/2026" or "1,234 دينار" when
// they sit inside Arabic text. The isolate characters are invisible and survive
// into the PDF and WhatsApp output, where the same reordering would happen.

const LRI = '⁦'; // left-to-right isolate
const PDI = '⁩'; // pop directional isolate

/** Pins a run of text to left-to-right order inside right-to-left surroundings. */
export const ltr = (text) => `${LRI}${text}${PDI}`;

const nf = (min, max) => new Intl.NumberFormat('en-US', { minimumFractionDigits: min, maximumFractionDigits: max });

const iqdFormat = nf(0, 0);
const usdFormat = nf(2, 2);
const volumeFormat = nf(0, 2);
const tonFormat = nf(0, 3);

export const CURRENCIES = ['IQD', 'USD'];
export const LITERS_PER_BARREL = 220;

/** A bare number with separators, isolated for use inside Arabic text. */
export const num = (value, decimals = null) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '-';
  return ltr(decimals === null ? n.toLocaleString('en-US') : nf(decimals, decimals).format(n));
};

export function money(value, currency) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '-';
  const digits = currency === 'USD' ? usdFormat.format(n) : iqdFormat.format(n);
  return `${ltr(digits)} ${currencyLabel[currency] || currency}`;
}

/** Renders both currencies, hiding whichever is zero. Shows "0" if both are. */
export function moneyPair(iqd, usd, { zero = '0' } = {}) {
  const parts = [];
  if (Number(iqd ?? 0) !== 0) parts.push(money(iqd, 'IQD'));
  if (Number(usd ?? 0) !== 0) parts.push(money(usd, 'USD'));
  return parts.length ? parts.join('  ·  ') : zero;
}

export const liters = (value) => `${ltr(volumeFormat.format(Number(value ?? 0)))} لتر`;
export const tons = (value) => `${ltr(tonFormat.format(Number(value ?? 0)))} طن`;
export const barrels = (value) => `${ltr(volumeFormat.format(Number(value ?? 0)))} برميل`;

export function quantityLabel(unit, quantity) {
  return unit === 'barrel' ? barrels(quantity) : tons(quantity);
}

export const percent = (value) => `${ltr(nf(0, 1).format(Number(value ?? 0)))}٪`;

export function formatDate(value) {
  if (!value) return '-';
  const text = String(value).slice(0, 10);
  const [y, m, d] = text.split('-');
  if (!y || !m || !d) return ltr(text);
  return ltr(`${d}/${m}/${y}`);
}

/** Date and time together, for backup files and queued entries. */
export function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return ltr(String(value));
  const pad = (n) => String(n).padStart(2, '0');
  return ltr(
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(
      date.getMinutes(),
    )}`,
  );
}

export const today = () => new Date().toISOString().slice(0, 10);

export function daysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

export const startOfMonth = (ref = today()) => `${ref.slice(0, 7)}-01`;

export function monthLabel(value) {
  if (!value) return '-';
  const [y, m] = String(value).slice(0, 7).split('-');
  return `${monthNames[Number(m) - 1] ?? m} ${ltr(y)}`;
}

export const unitText = (unit) => unitLabel[unit] || unit;

/**
 * Barrels to litres without going through a float, so the figure shown on the
 * form is character-for-character the one the server stores.
 */
export function litersFromBarrels(quantity) {
  const text = String(quantity ?? '').trim();
  if (!text || !/^\d*(\.\d*)?$/.test(text)) return '';
  const [whole, frac = ''] = text.split('.');
  const scaled = BigInt((whole || '0') + frac.padEnd(3, '0').slice(0, 3)) * BigInt(LITERS_PER_BARREL);
  const digits = scaled.toString().padStart(4, '0');
  return `${digits.slice(0, -3)}.${digits.slice(-3)}`.replace(/\.?0+$/, '') || '0';
}

/**
 * Litres to barrels without going through a float - truncated (not rounded)
 * to 3 decimals, so the figure shown on the form always agrees exactly with
 * what the server derives. Division rarely lands on a round number, unlike
 * the barrels -> litres direction above.
 */
export function barrelsFromLiters(litersValue) {
  const text = String(litersValue ?? '').trim();
  if (!text || !/^\d*(\.\d*)?$/.test(text)) return '';
  const [whole, frac = ''] = text.split('.');
  const milliLiters = BigInt((whole || '0') + frac.padEnd(3, '0').slice(0, 3));
  const milliBarrels = milliLiters / BigInt(LITERS_PER_BARREL); // truncates toward zero
  const digits = milliBarrels.toString().padStart(4, '0');
  return `${digits.slice(0, -3)}.${digits.slice(-3)}`.replace(/\.?0+$/, '') || '0';
}

/** Strips everything but digits so a stored phone works in a wa.me link. */
export function whatsappNumber(phone, countryCode = '964') {
  if (!phone) return '';
  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = countryCode + digits.slice(1);
  else if (!digits.startsWith(countryCode)) digits = countryCode + digits;
  return digits;
}

/** File-name-safe slug; Arabic names fall back to a stable transliteration-free id. */
export function fileSlug(text, fallback = 'file') {
  const slug = String(text || '')
    .trim()
    .replace(/[\s/\\:*?"<>|]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || fallback;
}
